/**
 * 综合 PO：按 online 真实 data 结构做展示归一化（主表衍生列 + 推送数据子表）
 */
import { parseJsonValue } from '@/utils/jsonNestedTable';

const PUSH_LABELS = {
  real_num: '实际数量',
  item_name: '计费项',
  idcName_hy: '鸿云机房',
  idcp_name: '机房',
  ticket_id: '工单号',
  supplierId: '服务商ID',
  supplier_name: '服务商',
  ticket_status: '工单状态',
  planCount: '计划数量',
  idcName: '机房单元',
  serverSn: '服务器SN',
  ticketId: 'DCOPS工单号',
  taskTypeName: '任务类型',
  orderScene: '工单场景',
  customerType: '客户类型',
  strategy: '处理说明',
  diagnoseClue: '诊断建议',
  eventDescription: '故障说明',
  callId: '呼叫ID',
  respCode: '响应码',
  respDesc: '响应说明',
  finalResult: '终审结果',
  reviewTicketId: '复核单号',
  ticketId_reform: '改造工单号',
  serverRack: '机架位',
  aimTxModel: '目标机型',
  complexity: '复杂度',
  reformType: '改造类型',
  poTicketId: 'PO单号(明细)',
  scanEndTime: '扫描结束时间',
};

/** 推送 data 弹窗内优先展示的列（对象转单行表时） */
const OBJECT_TABLE_PRIORITY = {
  serverDiagnose: [
    'serverSn',
    'ticketId',
    'idcName',
    'slaLevel',
    'taskTypeName',
    'orderScene',
    'createTime',
    'expireTime',
    'strategy',
  ],
  serverRepaire: ['respCode', 'respDesc', 'callId'],
  itReformCheck: [
    'ticketId',
    'serverSn',
    'idcName',
    'poTicketId',
    'reformType',
    'complexity',
    'serverRack',
    'aimTxModel',
    'scanEndTime',
    'despatchTime',
  ],
};

function isPlainObject(val) {
  return val !== null && typeof val === 'object' && !Array.isArray(val);
}

/** 搬迁计划：[{ "机房名": 3 }] -> [{ idcName_hy, planCount }] */
function normalizeMovePlanArray(arr) {
  return arr.map((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
    const keys = Object.keys(item);
    if (keys.length === 1 && (typeof item[keys[0]] === 'number' || typeof item[keys[0]] === 'string')) {
      return { idcName_hy: keys[0], planCount: item[keys[0]] };
    }
    return item;
  });
}

/** 维修推送：{ data: { callId }, respCode, respDesc } -> 扁平一行 */
function flattenRepairePush(data) {
  if (!isPlainObject(data)) return data;
  const row = {
    respCode: data.respCode,
    respDesc: data.respDesc,
    callId: data.data?.callId ?? data.callId,
  };
  return [row];
}

/** 按 Tab 将推送 JSON 转为 JsonNestedTable 友好结构 */
export function normalizePushDataForView(tabName, raw) {
  const data = parseJsonValue(raw);
  if (data === null || data === undefined) return data;

  switch (tabName) {
    case 'serverMoveInfo':
      if (Array.isArray(data)) return normalizeMovePlanArray(data);
      break;
    case 'serverRepaire':
      if (isPlainObject(data) && (data.respCode !== undefined || data.data)) {
        return flattenRepairePush(data);
      }
      break;
    case 'serverDiagnose':
    case 'itReformCheck':
      if (isPlainObject(data)) {
        const priority = OBJECT_TABLE_PRIORITY[tabName];
        if (priority) {
          const row = {};
          priority.forEach((k) => {
            if (data[k] !== undefined && data[k] !== null && data[k] !== '') row[k] = data[k];
          });
          Object.keys(data).forEach((k) => {
            if (!(k in row)) row[k] = data[k];
          });
          return [row];
        }
        return [data];
      }
      break;
    case 'poCost':
    case 'directDispatch':
    case 'crossAuditPush':
      if (Array.isArray(data)) return data;
      if (isPlainObject(data)) return [data];
      break;
    default:
      break;
  }
  return data;
}

export function getPushDataFieldLabels(tabName) {
  const extra = { ...PUSH_LABELS };
  if (tabName === 'serverRepaire') {
    extra.callId = '呼叫ID';
  }
  return extra;
}

/** 单行 key:value 弹窗字段顺序 */
export function getPushDataFieldPriority(tabName) {
  return OBJECT_TABLE_PRIORITY[tabName] || [];
}

export function getPushDialogTitle(tabName) {
  const map = {
    networkRush: '催办详情',
    serverDiagnose: '诊断工单明细',
    serverRepaire: '维修回执',
    serverMoveInfo: '搬迁计划（按机房）',
    poCost: '计费确认明细',
    directDispatch: '直配工单明细',
    itReformCheck: '改造任务明细',
    serverRepaireAdjustSla: '调级推送明细',
    crossAuditPush: '交叉审计推送明细',
  };
  return map[tabName] || '推送数据';
}

/** 主表行：从 data JSON 解析常用展示字段 */
export function enrichPoOtherRow(tabName, row) {
  if (!row) return row;
  const out = { ...row };
  const data = parseJsonValue(row.data);

  switch (tabName) {
    case 'serverDiagnose':
      if (isPlainObject(data)) {
        out._serverSn = data.serverSn;
        out._dcopsTicketId = data.ticketId;
        out._taskTypeName = data.taskTypeName;
        out._idcUnit = data.idcName;
      }
      break;
    case 'serverRepaire':
      if (isPlainObject(data)) {
        out._respCode = data.respCode;
        out._respDesc = data.respDesc;
        out._callId = data.data?.callId;
      }
      break;
    case 'serverMoveInfo':
      if (Array.isArray(data)) {
        out._planIdcCount = data.length;
        out._planTotal = data.reduce((sum, item) => {
          if (!item || typeof item !== 'object') return sum;
          const k = Object.keys(item)[0];
          return sum + (k ? Number(item[k]) || 0 : 0);
        }, 0);
      }
      break;
    case 'poCost':
      if (Array.isArray(data)) {
        out._billingLineCount = data.length;
        const total = data.reduce((s, it) => s + (Number(it?.real_num) || 0), 0);
        out._billingTotalQty = total;
      }
      break;
    case 'directDispatch':
      if (Array.isArray(data)) {
        out._dispatchLineCount = data.length;
      } else if (isPlainObject(data)) {
        out._dispatchLineCount = 1;
      }
      break;
    case 'itReformCheck':
      if (isPlainObject(data)) {
        out._reformTicketId = data.ticketId;
        out._reformServerSn = data.serverSn;
        out._reformIdc = data.idcName;
      }
      break;
    default:
      break;
  }
  return out;
}

export function pushDataSummary(tabName, raw) {
  const data = parseJsonValue(raw);
  if (data === null || data === undefined) return '';
  if (Array.isArray(data)) return `${data.length} 条`;
  if (isPlainObject(data)) return '1 条对象';
  return '';
}
